import React, { useState } from "react";
import { AppRole } from "@microsoft/microsoft-graph-types";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemText,
  IconButton,
  TextField,
  Button,
  Stack,
  Box,
  Tooltip,
  Chip,
  ListItemAvatar,
  Avatar
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SecurityIcon from "@mui/icons-material/Security";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

interface AppRolesProps {
  appRoles: AppRole[];
  appId?: string;
}

const AppRoles: React.FC<AppRolesProps> = ({
  appRoles,
  appId
}) => {
  // Accordion state
  const [rolesExpanded, setRolesExpanded] = useState(true);

  const handleRolesAccordionChange = (_event: React.SyntheticEvent, isExpanded: boolean) => {
    setRolesExpanded(isExpanded);
  };

  const getMemberTypeLabel = (allowedMemberTypes?: string[] | null): string => {
    if (!allowedMemberTypes || allowedMemberTypes.length === 0) return "Users/Groups";
    if (allowedMemberTypes.includes("User") && allowedMemberTypes.includes("Application")) return "Users/Groups & Applications";
    if (allowedMemberTypes.includes("User")) return "Users/Groups";
    if (allowedMemberTypes.includes("Application")) return "Applications";
    return "Not specified";
  };

  return (
    <Card variant="outlined" sx={{ mb: 4 }}>
      <CardContent>
        <Accordion
          expanded={rolesExpanded}
          onChange={handleRolesAccordionChange}
          sx={{
            border: '1px solid #e0e7ff', // Indigo border like OptionalClaims
            borderRadius: '8px !important',
            '&:before': { display: 'none' },
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)', // Same shadow as OptionalClaims
            mb: 2
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              background: 'linear-gradient(135deg, #e8eaf6 0%, #f3e5f5 100%)',
              borderBottom: rolesExpanded ? '1px solid #e0e7ff' : 'none',
              minHeight: '64px' // Same height as OptionalClaims
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <Box sx={{
                width: 40, // Same size as OptionalClaims
                height: 40,
                borderRadius: '50%',
                backgroundColor: '#3f51b5', // Indigo background
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mr: 2
              }}>
                <SecurityIcon sx={{ color: 'white', fontSize: '1.2rem' }} />
              </Box>
              <Typography
                variant="h6"
                sx={{
                  fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
                  fontSize: "1.25rem", // Same as OptionalClaims
                  fontWeight: 600,
                  color: "#283593", // Indigo text color
                  letterSpacing: "0.02em",
                  flexGrow: 1
                }}
              >
                App Roles
              </Typography>
              <Chip
                size="small"
                label={`${appRoles.length} roles`}
                sx={{
                  mr: 1,
                  backgroundColor: '#e3f2fd', // Light blue background
                  color: '#1565c0', // Dark blue text
                  fontWeight: 500,
                  border: '1px solid #bbdefb'
                }}
              />
              {appId && (
                <Tooltip title="Configure in Entra Portal">
                  <IconButton
                    size="small"
                    component="a"
                    href={`https://portal.azure.com/#view/Microsoft_AAD_RegisteredApps/ApplicationMenuBlade/~/AppRoles/appId/${appId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e: React.MouseEvent) => e.stopPropagation()}
                    sx={{ ml: 1, color: '#5c6bc0' }}
                  >
                    <OpenInNewIcon sx={{ fontSize: '1rem' }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </AccordionSummary>
          <AccordionDetails
            sx={{
              p: 0,
              backgroundColor: "#fafbff", // Light indigo background like OptionalClaims
            }}
          >
            {appRoles.length === 0 ? (
              <Box sx={{
                p: 4,
                m: 2,
                backgroundColor: '#f8f9fa',
                borderRadius: '12px',
                textAlign: 'center',
                border: '2px dashed #dee2e6'
              }}>
                <Box sx={{
                  width: 64,
                  height: 64,
                  borderRadius: '50%',
                  backgroundColor: '#e9ecef',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 2
                }}>
                  <SecurityIcon sx={{ fontSize: '2rem', color: '#6c757d' }} />
                </Box>
                <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                  No app roles configured
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  This application does not have any app roles configured.
                </Typography>
              </Box>
            ) : (
              <List sx={{ p: 2 }}>
                {appRoles.map((role) => (
                  <ListItem
                    key={role.id}
                    sx={{
                      mb: 2,
                      p: 2,
                      borderRadius: '8px',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.07)',
                      backgroundColor: 'white',
                      borderLeft: `4px solid ${role.isEnabled ? '#4caf50' : '#f44336'}`
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: role.isEnabled ? '#e8f5e9' : '#ffebee' }}>
                        {role.isEnabled
                          ? <CheckCircleOutlineIcon color="success" />
                          : <HighlightOffIcon color="error" />
                        }
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.primary' }}>
                          {role.displayName}
                        </Typography>
                      }
                      secondary={
                        <Stack spacing={0.5} sx={{ mt: 1 }}>
                          <Typography component="span" variant="body2" color="text.secondary">
                            <strong>Value:</strong> {role.value}
                          </Typography>
                          <Typography component="span" variant="body2" color="text.secondary">
                            <strong>Description:</strong> {role.description || "No description provided."}
                          </Typography>
                          <Typography component="span" variant="body2" color="text.secondary">
                            <strong>Allowed for:</strong> {getMemberTypeLabel(role.allowedMemberTypes)}
                          </Typography>
                        </Stack>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            )}

            <Box sx={{
              m: 2,
              borderRadius: '8px',
              overflow: 'hidden',
              boxShadow: '0 1px 4px rgba(0, 0, 0, 0.04)',
              opacity: 0.85
            }}>
              <Accordion sx={{ '&:before': { display: 'none' }, borderRadius: '8px !important' }}>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ fontSize: '1.2rem', color: '#9e9e9e' }} />}
                  sx={{
                    background: '#f8f9fa',
                    borderBottom: '1px solid #e9ecef',
                    minHeight: '48px',
                    '&:hover': { backgroundColor: '#f1f3f4' }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <InfoOutlinedIcon sx={{ mr: 1.5, fontSize: "1.1rem", color: 'info.main' }} />
                    <Typography variant="subtitle1" sx={{ fontSize: "1rem", fontWeight: 500 }}>
                      About App Roles
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ p: 2, backgroundColor: "#f5f5ff", borderRadius: 1 }}>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      App roles are custom roles you define for your application to assign permissions to users or other applications. They are a form of role-based access control (RBAC).
                    </Typography>
                    <Box sx={{ p: 2, backgroundColor: '#ffffff', border: '1px solid #e0e0ff', borderRadius: 1, mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ color: '#0d47a1', mb: 1 }}>
                        How It Works
                      </Typography>
                      <Typography variant="body2">
                        • <strong>Definition:</strong> You define roles like "Reader" or "Contributor" in the application manifest.<br />
                        • <strong>Assignment:</strong> An administrator assigns users, groups, or applications to these roles.<br />
                        • <strong>In Tokens:</strong> When an assigned user or application signs in, their assigned roles are included in the `roles` claim of their ID or access token.
                      </Typography>
                    </Box>
                  </Box>
                </AccordionDetails>
              </Accordion>
            </Box>
          </AccordionDetails>
        </Accordion>
      </CardContent>
    </Card>
  );
};

export default AppRoles;